/**
 * Tiny standalone theme helper for the status workspace.
 *
 * We don't pull in zustand here — there's only one piece of state
 * (theme) and one event (toggle). A pair of plain functions + a small
 * subscriber registry is cheaper than a store.
 */

import { flushSync } from "react-dom";

export type Theme = "dark" | "light";

const KEY = "dr_theme";

function detectInitial(): Theme {
  try {
    const v = localStorage.getItem(KEY);
    if (v === "dark" || v === "light") return v;
    return window.matchMedia("(prefers-color-scheme: light)").matches
      ? "light"
      : "dark";
  } catch {
    return "dark";
  }
}

let current: Theme = detectInitial();
const listeners = new Set<(t: Theme) => void>();

function apply(theme: Theme): void {
  document.documentElement.setAttribute("data-theme", theme);
  document.documentElement.classList.toggle("dark", theme === "dark");
}

export function bootstrapTheme(): void {
  apply(current);
}

export function getTheme(): Theme {
  return current;
}

export function subscribeTheme(cb: (t: Theme) => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function toggleTheme(): void {
  const next: Theme = current === "dark" ? "light" : "dark";
  const fn = () => {
    current = next;
    try {
      localStorage.setItem(KEY, next);
    } catch {
      /* ignore */
    }
    apply(next);
    for (const l of listeners) l(next);
  };
  const doc = document as Document & {
    startViewTransition?: (cb: () => void) => { finished: Promise<void> };
  };
  if (typeof doc.startViewTransition === "function") {
    doc.startViewTransition(() => flushSync(fn));
  } else {
    fn();
  }
}
